import { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from '../../../ui/card';
import { Label } from '../../../ui/label';
import { JobItem } from './JobItem';
import { JobDetailsDialog } from "../../shared/JobDetailsDialog";
import { Loader } from "../../../base/Loader";
import { findJob } from '../../../../apiManager/shoveller/matchJobApi';
import { GoogleMap, useJsApiLoader, Marker, Autocomplete } from "@react-google-maps/api";
import ShovelHouseImage from "../../../../assets/images/shovelhouse.png";
import { AlertCircle } from "lucide-react";

const libraries = ["places"];

export function MapView() {
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const shovellerId = userInfo.user.id;

  const [jobs, setJobs] = useState(new Map());
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [error, setError] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  
  const initialLat = userInfo.user.latitude || 43.6532;
  const initialLng = userInfo.user.longitude || -79.3832;
  const [position, setPosition] = useState({ lat: initialLat, lng: initialLng });
  
  const [map, setMap] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [location, setLocation] = useState({ address: '', lat: null, lng: null });

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDJQNAbTK3AGLlmRGVxa3VbejegSp-qB9A",
    libraries
  });

  // Map Functions
  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    
    if (window.google && window.google.maps) {
      setGeocoder(new window.google.maps.Geocoder());
      
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (location) => {
            const { latitude, longitude } = location.coords;
            const newPosition = { lat: latitude, lng: longitude };
            setPosition(newPosition);
            mapInstance.panTo(newPosition);
            fetchNearbyJobs(latitude, longitude);
            reverseGeocode(latitude, longitude);
          },
          (error) => {
            console.error("Error getting location", error);
            fetchNearbyJobs(initialLat, initialLng);
          }
        );
      } else {
        fetchNearbyJobs(initialLat, initialLng);
      }
    }
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const onAutocompleteLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const handlePlaceSelect = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const { lat, lng } = place.geometry.location;
        const newPosition = { lat: lat(), lng: lng() };
        setPosition(newPosition);
        if (map) map.panTo(newPosition);
        fetchNearbyJobs(lat(), lng());
        if (place.formatted_address) {
          setLocation({ address: place.formatted_address, lat: lat(), lng: lng() });
        }
      }
    }
  };

  const reverseGeocode = (lat, lng) => {
    if (geocoder && window.google && window.google.maps) {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const newAddress = results[0].formatted_address;
          setLocation({ address: newAddress, lat: lat, lng: lng });
        } else {
          console.error("Geocoder failed due to: " + status);
        }
      });
    }
  };

  const debounce = (func, timeout = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  };

  const handleMapCenterChanged = useCallback(
    debounce(() => {
      if (map) {
        const newPosition = map.getCenter();
        const newLat = newPosition.lat();
        const newLng = newPosition.lng();

        if (newLat !== position?.lat || newLng !== position?.lng) {
          setPosition({ lat: newLat, lng: newLng });
          reverseGeocode(newLat, newLng);
          fetchNearbyJobs(newLat, newLng);
        }
      }
    }, 1000),
    [map, position, geocoder]
  );

  const fetchNearbyJobs = async (lat, lng) => {
    try {
      setLoadingJobs(true);
      const res = await findJob(lat, lng);
      if (res.error) {
        setError(res.error);
        setJobs(new Map());
      } else {
        setError('');
        const jobsMap = new Map();
        res.jobs.filter(job => !job.ShovelerInfo?.some(shoveller => shoveller.ShovelerId === shovellerId))
          .forEach(job => jobsMap.set(job._id, job));
        setJobs(jobsMap);
      }
    } catch (error) {
      console.log(error);
      setError(error.message || "An error occurred while fetching jobs");
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowJobDetails(true);
  };

  const navigateToHouseOwnerJob = (job) => {
    navigate("/shoveller/isMatchHouseOwner", {
      state: {
        houseOwnerId: job.houseOwnerId._id,
        jobId: job._id,
        scheduledTime: job.scheduledTime,
        name: job.houseOwnerId.userName,
        serviceInstruction: job.serviceDetail.serviceInstruction[0],
        isRequestedForVolunteer: job.isRequestedForVolunteer
      }
    });
  };

  const handleAcceptJob = () => {
    if (selectedJob) {
      navigateToHouseOwnerJob(selectedJob);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Location */}
      <Card className="p-6">
        <CardContent className="p-0">
          <div className="space-y-4">
            <Label className="text-xl flex items-center gap-3">
              Search Location
            </Label>
            <div className="w-full flex gap-4 items-center bg-zinc-100 p-3 rounded-lg">
              {isLoaded ? (
                <Autocomplete
                  className="w-full"
                  onLoad={onAutocompleteLoad}
                  onPlaceChanged={handlePlaceSelect}
                >
                  <input
                    type="text"
                    value={location.address}
                    placeholder="Search for a location"
                    onChange={(e) => setLocation({ ...location, address: e.target.value })}
                    className="w-full bg-transparent outline-none text-lg"
                  />
                </Autocomplete>
              ) : (
                <input
                  type="text"
                  value={location.address}
                  placeholder="Search for a location"
                  onChange={(e) => setLocation({ ...location, address: e.target.value })}
                  className="w-full bg-transparent outline-none text-lg"
                  disabled
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Map View */}
      {!isLoaded ? (
        <Loader />
      ) : (
        <Card className="p-0">
          <CardContent className="p-0">
            <div className="h-96 bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-300">
              <GoogleMap
                onLoad={onLoad}
                onUnmount={onUnmount}
                onCenterChanged={handleMapCenterChanged}
                center={position || { lat: 43.6532, lng: -79.3832 }}
                zoom={14}
                mapContainerStyle={{ width: "100%", height: "100%" }}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false
                }}
              >
                {position && (
                  <Marker
                    position={position}
                    draggable={true}
                    onDragEnd={(event) => {
                      const newLat = event.latLng.lat();
                      const newLng = event.latLng.lng();
                      setPosition({ lat: newLat, lng: newLng });
                      reverseGeocode(newLat, newLng);
                      fetchNearbyJobs(newLat, newLng);
                    }}
                  />
                )}

                {/* Job Markers */}
                {Array.from(jobs.values()).map((job) => (
                  <Marker
                    key={job._id}
                    position={{
                      lat: Number(job.location.coordinates[1]),
                      lng: Number(job.location.coordinates[0])
                    }}
                    icon={{
                      url: ShovelHouseImage,
                      scaledSize: new window.google.maps.Size(30, 30),
                    }}
                    title={job.serviceDetail.service}
                    onClick={() => handleJobClick(job)}
                  />
                ))}
              </GoogleMap>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Message */}
      {error && (
  <div className="flex justify-center items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg animate-in slide-in-from-top duration-300">
    <AlertCircle className="w-5 h-5 text-gray-600 flex-shrink-0" />
    <p className="text-gray-700">{error}</p>
  </div>
)}

      {/* Job List for Map View */}
      <div className="space-y-4">
        {loadingJobs ? (
          <Loader />
        ) : (
          Array.from(jobs.values()).map((job) => (
            <JobItem 
              key={job._id} 
              job={job}
            />
          ))
        )}
      </div>
    </div>
  );
}